/**
 * @file lightingDomain.ts
 * @author Rapoo Device SDK Team
 * @date 2026-06-22
 * @description 灯效领域驱动架构核心类型定义
 */

import { RGB, RGBGroup } from './color'
import { CommonAddress } from './protocol'

/**
 * 灯效领域类型枚举
 * @description 定义不同的灯效领域分类
 */
export enum LightingDomainType {
  /** 静态灯效（固定颜色和效果） */
  Static = 'static',
  /** 动态灯效（动画效果） */
  Dynamic = 'dynamic',
  /** 侧边灯效 */
  Side = 'side',
  /** 指示灯效 */
  Indicator = 'indicator',
  /** RGB灯效 */
  Rgb = 'rgb',
}

/**
 * 灯效属性标志位
 * @description 使用位掩码方式标识灯效支持的属性特性
 */
export const enum LightingFeatureFlag {
  /** 无特殊属性 */
  None = 0,
  /** 支持亮度调节 (bit 0) */
  Brightness = 1 << 0,
  /** 支持速度调节 (bit 1) */
  Speed = 1 << 1,
  /** 支持单色模式 (bit 2) */
  SingleColor = 1 << 2,
  /** 支持多彩模式 (bit 3) */
  Colorful = 1 << 3,
  /** 支持渐变效果 (bit 4) */
  Gradient = 1 << 4,
  /** 支持按键映射 (bit 5) */
  KeyMap = 1 << 5,
  /** 支持名称配置 (bit 6) */
  Name = 1 << 6,
}

/**
 * 灯效配置数据（统一结构）
 * @description 所有灯效领域的统一配置数据结构
 */
export interface LightingConfigData {
  /** 配置索引，用于标识不同的灯效配置项 */
  index: number
  /** 灯效领域类型 */
  domainType: LightingDomainType
  /**
   * 亮度值（可选），范围通常为 0-100
   * @description 用于控制灯效亮度，默认值为 60
   * @default 60
   */
  brightness?: number
  /**
   * 速度值（可选），用于动态灯效的速度控制
   * @description 范围通常为 0-100，默认值为 60
   * @default 60
   */
  speed?: number
  /**
   * 是否为多彩模式（可选）
   * @description 用于动态和侧边灯效的模式控制
   * - true: 多彩模式（buffer[2] = 0）
   * - false: 单色模式（buffer[2] = 1）
   * @default false
   */
  isColorful?: boolean
  /** RGB 颜色值（可选），用于单色灯效 */
  rgb?: RGB
  /** RGB 颜色组（可选），用于多彩或渐变灯效 */
  rgbGroup?: RGBGroup
  /**
   * 动态灯效运动方向（可选），正向/反向（0=正向, 1=反向）。
   * 用于波浪/扩散/回旋等动态灯效，对应芯片功能变量区方向字段
   * （GCOME_AP 背光方向偏移；其他芯片无此字段时序列化忽略）。
   */
  direction?: number | boolean
  /**
   * 渐变方向（可选），用于静态灯效的渐变控制
   * @description 取值说明：
   * - 1: 单色模式（默认）
   * - 2: 左右渐变（双色）
   * - 3: 其他渐变模式（双色）
   * @default 1
   */
  gradientDirection?: number
  /**
   * 原始缓冲区数据（可选）
   * @description 存储原始的 8 字节二进制数据
   * @remarks 所有灯效配置的大小固定为 8 字节（configSize = 8）
   * 用于未完全解析的场景或直接写入设备
   */
  buffer?: Uint8Array
}

/**
 * 地址解析策略
 * @description 用于将灯效配置数据映射到设备内存地址的接口
 */
export interface AddressResolver {
  /**
   * 获取基础地址
   * @param configIndex 配置索引
   * @param itemIndex 项索引
   * @returns 基础内存地址
   */
  getBaseAddr(configIndex: number, itemIndex: number): CommonAddress
  /**
   * 获取字段地址
   * @param field 字段名称
   * @param configIndex 配置索引
   * @param itemIndex 项索引
   * @returns 字段对应的内存地址
   */
  getFieldAddr(field: string, configIndex: number, itemIndex: number): CommonAddress
}

/**
 * 缓冲区 <-> 结构化数据互转
 * @description 负责将二进制缓冲区数据解析为结构化配置数据，以及将配置数据序列化为二进制数据
 */
export interface LightingParser {
  /**
   * 解析缓冲区数据
   * @param buffer 原始二进制缓冲区
   * @param index 配置索引
   * @returns 解析后的灯效配置数据
   */
  parse(buffer: Uint8Array, index: number): LightingConfigData
  /**
   * 序列化配置数据
   * @param config 灯效配置数据
   * @returns 序列化后的二进制缓冲区
   */
  serialize(config: LightingConfigData): Uint8Array
}

/**
 * 自定义灯效读取策略
 * @description 用于读取和解析设备上自定义灯效的配置信息
 */
export interface CustomEffectResolver {
  /** 灯效名称的内存地址数组 */
  nameAddress: CommonAddress[]
  /** 需要读取的数据大小（字节数） */
  readSize: number
  /**
   * 解析灯效名称
   * @param data 数据视图
   * @returns 灯效名称字符串
   */
  parseName(data: DataView): string
  /**
   * 解析灯效类型
   * @param data 数据视图
   * @returns 灯效类型编号
   */
  parseType(data: DataView): number
}

/**
 * 领域描述符
 * @description 描述一个灯效领域的完整配置信息，包括类型、大小、支持特性、地址解析和解析器
 */
export interface LightingDomainDescriptor {
  /** 灯效领域类型 */
  type: LightingDomainType
  /**
   * 单个配置项的大小（字节数）
   * @description 所有灯效配置项统一为 8 字节
   * @remarks 根据 staticDomain、dynamicDomain、sideDomain 的实现，configSize 均为 8
   * @default 8
   */
  configSize: number
  /** 支持的灯效特性标志（位掩码） */
  supportedFeatures: LightingFeatureFlag
  /** 地址解析策略，用于将配置映射到设备内存 */
  addressResolver: AddressResolver
  /** 数据解析器，用于缓冲区和结构化数据之间的转换 */
  parser: LightingParser
  /**
   * 规范化索引（可选）
   * @param index 原始索引
   * @returns 规范化后的索引
   */
  normalizeIndex?(index: number): number
  /** 是否支持自定义灯效（可选） */
  hasCustomEffects?: boolean
  /** 自定义灯效解析器（可选），当 hasCustomEffects 为 true 时需要提供 */
  customEffectResolver?: CustomEffectResolver
  /** 缓存标签（可选）, index: 效果索引, configIndex: 配置索引 */
  cacheTags?(index: number, configIndex: number): string[]
}
